// The display-scale control: a percentage slider with a few preset stops.
//
// Replaces the S/M/L segmented groups (see src/state/displayScale.ts for why).
// One component so the workbench's view-settings popover and Score Studio's
// toolbar cannot drift apart the way their two S/M/L ladders did.

import {
  SCALE_MAX,
  SCALE_MIN,
  SCALE_PRESETS,
  SCALE_STEP,
  scaleLabel,
} from '@/state/displayScale';

interface ScaleSliderProps {
  /** Current percentage. */
  value: number;
  onChange: (percent: number) => void;
  /** Accessible name, e.g. "Score size". */
  label: string;
  /** Prefix for the testids, e.g. `score-scale` → `score-scale-slider`. */
  testid: string;
  /** Hide the preset row where space is tight (the Studio toolbar). */
  presets?: boolean;
}

export function ScaleSlider({
  value,
  onChange,
  label,
  testid,
  presets = true,
}: ScaleSliderProps) {
  return (
    <div className="scale-slider" data-testid={testid}>
      <div className="scale-slider-row">
        <input
          type="range"
          min={SCALE_MIN}
          max={SCALE_MAX}
          step={SCALE_STEP}
          value={value}
          aria-label={label}
          data-testid={`${testid}-slider`}
          onChange={(e) => onChange(Number(e.target.value))}
        />
        <span className="scale-slider-value" data-testid={`${testid}-value`}>
          {scaleLabel(value)}
        </span>
      </div>
      {presets && (
        <div className="scale-slider-presets" role="group" aria-label={`${label} presets`}>
          {SCALE_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className={value === preset ? 'active' : ''}
              aria-pressed={value === preset}
              data-testid={`${testid}-preset-${preset}`}
              onClick={() => onChange(preset)}
              title={`${preset}%`}
            >
              {preset}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
